/**
 * useCarpool — POST /api/carpool/nearby, cache results, expose book().
 *
 * The rider anchor is passed in (map center) so the hook re-fetches when
 * the user pans significantly. We debounce anchor changes at 800ms so
 * pan storms don't hammer OSRM.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getBaseUrl } from "../lib/api";
export function useCarpool({ lat, lng, count = 6, enabled = true, }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const bookedIdsRef = useRef(new Set());
    const fetchNearby = useCallback(async () => {
        if (!enabled)
            return;
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${getBaseUrl()}/api/carpool/nearby`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ lat, lng, count }),
                signal: ac.signal,
            });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const json = await resp.json();
            // Apply any local bookings.
            json.drivers.forEach((d) => {
                if (bookedIdsRef.current.has(d.id))
                    d.booked = true;
            });
            setData(json);
        }
        catch (e) {
            if (e.name !== "AbortError") {
                setError(String(e.message ?? e));
            }
        }
        finally {
            setLoading(false);
        }
    }, [lat, lng, count, enabled]);
    const lastFetchAnchor = useRef(null);
    useEffect(() => {
        // Q1: 800ms debounce (matches header comment). Also skip refetch when
        // the anchor barely moved — < 100m — to prevent pan cascades from
        // hammering OSRM with near-duplicate requests.
        const last = lastFetchAnchor.current;
        const nearDup = last !== null &&
            Math.abs(last.lat - lat) < 0.001 &&
            Math.abs(last.lng - lng) < 0.001;
        if (nearDup)
            return;
        const t = window.setTimeout(() => {
            lastFetchAnchor.current = { lat, lng };
            void fetchNearby();
        }, 800);
        return () => window.clearTimeout(t);
    }, [fetchNearby, lat, lng]);
    const book = useCallback(async (driverId) => {
        try {
            const resp = await fetch(`${getBaseUrl()}/api/carpool/book`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    driver_id: driverId,
                    rider_lat: lat,
                    rider_lng: lng,
                }),
            });
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const json = await resp.json();
            bookedIdsRef.current.add(driverId);
            setData((prev) => prev
                ? {
                    ...prev,
                    drivers: prev.drivers.map((d) => d.id === driverId ? { ...d, booked: true } : d),
                }
                : prev);
            return json;
        }
        catch (e) {
            setError(String(e.message ?? e));
            return null;
        }
    }, [lat, lng]);
    return { data, loading, error, refetch: fetchNearby, book };
}
